import { useEffect, useMemo, useState } from 'react';
import { MarketAccountContext, type MarketAccountState } from './useMarketAccount';
import { useAuth } from './AuthContext';
import forexApi, { type FollowerAccountDTO } from '@/services/forexApi';

export function MarketAccountProvider({ children }: { children: React.ReactNode }) {
  const { user, accessToken } = useAuth();
  const owner = user?.user_id || user?.id || '';
  const [data, setData] = useState({ owner: '', accounts: [] as FollowerAccountDTO[], loading: false, error: '' });
  const [selection, setSelection] = useState({ owner: '', id: '' });
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    if (!owner || !accessToken) return;
    const controller = new AbortController();
    let active = true;
    setData(previous => ({ owner, accounts: previous.owner === owner ? previous.accounts : [], loading: true, error: '' }));
    forexApi.listFollowerAccounts(accessToken, controller.signal).then(accounts => {
      if (!active) return;
      setData({ owner, accounts, loading: false, error: '' });
      setSelection(previous => ({ owner, id: previous.owner === owner && accounts.some(a => a.id === previous.id)
        ? previous.id : accounts.length === 1 ? accounts[0].id : '' }));
    }).catch(() => {
      if (active) setData({ owner, accounts: [], loading: false, error: '账号列表暂不可用' });
    });
    return () => { active = false; controller.abort(); };
  }, [owner, accessToken, refreshKey]);
  const accounts = data.owner === owner && !!accessToken ? data.accounts : [];
  const selected = selection.owner === owner ? accounts.find(a => a.id === selection.id) : undefined;
  const scope = useMemo(() => selected ? { accountId: selected.follower_account_id || selected.id } : undefined, [selected]);
  // Source data never survives an owner, account, platform, Broker or credential submission change.
  const scopeKey = JSON.stringify([owner, selected?.id, selected?.platform, selected?.broker_name,
    selected?.server, selected?.submission_revision ?? selected?.onboarding?.submission_revision]);
  const value: MarketAccountState = {
    accounts, selectedId: selected?.id || '', scope, scopeKey,
    loading: data.owner === owner && data.loading,
    error: data.owner === owner ? data.error : '',
    select: id => setSelection({ owner, id }), refresh: () => setRefreshKey(value => value + 1),
  };
  return <MarketAccountContext.Provider value={value}>{children}</MarketAccountContext.Provider>;
}
